import { useEffect, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { default as DefaultApp } from 'next/app';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { DpAnalytics } from '@greatmindsorg/dp-analytics-lib';
import getConfig from 'next/config';
import { AlertManager, AppContextState, AppStateProvider, AtomicAlertManager, AuthContextState, AuthStateProvider, DpErrorBoundary, ServiceContext, ServiceContextState, initializeAuth, isLocal, redirectToLogIn, useMount, initializePendo, isPendoReady, startPendoGuides, GlobalNavSelectedListItemProvider } from '@greatmindsorg/assess-web-core-lib';
import { baseTheme } from '@greatmindsorg/atomic-ui-lib-lts';
import atomicBaseTheme from '@greatmindsorg/atomic-ui-lib-lts/dist/themes/baseTheme';
import build from '@greatmindsorg/core-theme';
import 'styles/globals.scss';
import '@greatmindsorg/gmui-lib/dist/global.css';
import { AuthenticatedApp } from '~/modules/App/AuthenticatedApp';
import initI18n from '../i18n';
import { Ripl, useRipl } from '@greatmindsorg/ripl-react';
initI18n();
const theme = {
  ...build(),
  ...baseTheme
};
const {
  publicRuntimeConfig
} = getConfig();
function RootApp({
  Component,
  pageProps
}: AppProps) {
  // ----------------------------------------- State
  const [authState, setAuthState] = useState<AuthContextState>();
  const [initialized, setInitialized] = useState(false);
  const [serviceContextState, setServiceContextState] = useState<ServiceContextState>();
  const [appState, setAppState] = useState<AppContextState>();
  const userRipl = useRipl()?.getUser();

  // ------------------------------- Const
  const pendoReady = isPendoReady();

  // ------------------------------- Methods
  const initializeApplicationContext = async () => {
    const {
      authContextState,
      appContextState
    } = await initializeAuth({});
    const contextState = ServiceContextState.newInstance({
      authContextState
    });
    const {
      schools
    } = authContextState.user;
    const {
      licensedCurriculums
    } = appContextState;
    const curriculumShortCodes = licensedCurriculums.map(curriculum => curriculum.id);
    DpAnalytics({
      appName: 'dp-assessments-student-app',
      googleAnalyticsId: publicRuntimeConfig.GA4_MEASUREMENT_ID,
      debug: publicRuntimeConfig.NODE_ENV !== 'production'
    }).ready(() => {
      DpAnalytics?.()?.reset();
      DpAnalytics?.()?.identify(userRipl?.userID as string, {
        activeRole: userRipl?.activeRole,
        rolesAvailable: userRipl?.roles,
        districtId: userRipl?.districtID,
        districtName: userRipl?.districtName,
        //TODO:
        schoolIds: schools?.map(school => school.id),
        schoolNames: schools?.map(school => school.name),
        //Licenses short codes
        curriculaAvailable: curriculumShortCodes
      });
    });
    if (authContextState.isAuthenticated) {
      initializePendo({
        id: userRipl?.userID,
        districtId: userRipl?.districtID,
        schools: authContextState.user.schools,
        roles: userRipl?.roles
      }, curriculumShortCodes);
      setAuthState(authContextState);
      setServiceContextState(contextState);
      setAppState(appContextState);
      setInitialized(true);
    } else if (!isLocal()) {
      redirectToLogIn({
        location
      });
    }
  };
  useMount(() => {
    initializeApplicationContext();
  });
  useEffect(() => {
    if (pendoReady) {
      startPendoGuides();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendoReady]);

  // ----------------------------------------- Render
  return <ThemeProvider theme={theme}>
      <Head>
        <title>Great Minds - Assessments</title>
      </Head>
      <ThemeProvider theme={atomicBaseTheme}>
        <AlertManager>
          <AtomicAlertManager>
            <DpErrorBoundary appName="student-app">
              {initialized && <AuthStateProvider initialValue={authState}>
                  {authState.isAuthenticated ? <ServiceContext.Provider value={serviceContextState}>
                      <AppStateProvider initialValue={appState}>
                        <GlobalNavSelectedListItemProvider>
                          <AuthenticatedApp>
                            <Component {...pageProps} />
                          </AuthenticatedApp>
                        </GlobalNavSelectedListItemProvider>
                      </AppStateProvider>
                    </ServiceContext.Provider> : null}
                </AuthStateProvider>}
            </DpErrorBoundary>
          </AtomicAlertManager>
        </AlertManager>
      </ThemeProvider>
    </ThemeProvider>;
}
export default class App extends DefaultApp {
  static async getInitialProps({
    Component,
    ctx
  }) {
    const pageProps = Component.getInitialProps ? await Component.getInitialProps(ctx) : {};
    return {
      pageProps
    };
  }
  render() {
    function handleRiplFailure(e: unknown) {
      if (isLocal()) {
        console.log('Authentication failed', e);
      } else {
        redirectToLogIn({
          location
        });
      }
    }
    return <Ripl disableRefresh riplApiUrl={publicRuntimeConfig.NEXT_PUBLIC_RIPL_API_URL} onFailure={handleRiplFailure}>
        {/* @ts-ignore */}
        <RootApp {...this.props} />
      </Ripl>;
  }
}